import { apiRequest } from "./client";
import type { TaskStatus } from "./tasks";

interface ApiResponse<T> {
  status: "success";
  data: T;
}

export type VerifiedOutcome =
  | "unassessed"
  | "partial"
  | "verified_complete"
  | "verified_failed"
  | "inconclusive";

export interface CompletionCriterion {
  id: string;
  key: string;
  description: string;
  required: boolean;
  verification_kind: string;
  expected: Record<string, unknown>;
}

export interface TaskContract {
  id: string;
  version: number;
  schema_version: number;
  goal: string;
  authority: Record<string, unknown>;
  accepted_at: string;
  criteria: CompletionCriterion[];
}

export interface TaskWorkDetail {
  task_id: string;
  board_status: TaskStatus;
  execution_status: string | null;
  verified_outcome: VerifiedOutcome;
  contract: TaskContract | null;
}

export interface TaskWorkTimeline {
  task_id: string;
  board_status: TaskStatus;
  verified_outcome: VerifiedOutcome;
  contracts: TaskContract[];
  executions: Array<{
    id: string;
    status: string;
    outcome_state: VerifiedOutcome;
    contract_revision_id: string | null;
    contract_version: number | null;
    started_at: string | null;
    completed_at: string | null;
    created_at: string;
  }>;
  pagination: { has_more: boolean; next_cursor: string | null };
}

export interface TaskContractInput {
  schema_version?: 1;
  goal: string;
  authority?: Record<string, unknown>;
  criteria: Array<{
    key: string;
    description: string;
    required?: boolean;
    verification_kind: string;
    expected?: Record<string, unknown>;
  }>;
}

export async function getTaskWorkDetail(
  projectId: string,
  taskId: string,
): Promise<TaskWorkDetail> {
  const res = await apiRequest<ApiResponse<TaskWorkDetail>>(
    `/api/v1/projects/${projectId}/tasks/${taskId}/work/`,
  );
  return res.data;
}

export async function createTaskContractRevision(
  projectId: string,
  taskId: string,
  input: TaskContractInput,
): Promise<TaskContract> {
  const res = await apiRequest<ApiResponse<TaskContract>>(
    `/api/v1/projects/${projectId}/tasks/${taskId}/work/`,
    { method: "POST", body: input },
  );
  return res.data;
}

export async function getTaskWorkTimeline(
  projectId: string,
  taskId: string,
  options?: { cursor?: string; limit?: number },
): Promise<TaskWorkTimeline> {
  const search = new URLSearchParams();
  if (options?.cursor) search.set("cursor", options.cursor);
  if (options?.limit) search.set("limit", String(options.limit));
  const query = search.toString();
  const res = await apiRequest<ApiResponse<TaskWorkTimeline>>(
    `/api/v1/projects/${projectId}/tasks/${taskId}/work-timeline/${query ? `?${query}` : ""}`,
  );
  return res.data;
}

export async function submitTaskWorkVerification(
  projectId: string,
  taskId: string,
  input: { criterion_id: string; status: "passed" | "failed" },
): Promise<{ verification_id: string; outcome_state: VerifiedOutcome }> {
  const res = await apiRequest<
    ApiResponse<{ verification_id: string; outcome_state: VerifiedOutcome }>
  >(`/api/v1/projects/${projectId}/tasks/${taskId}/work-verification/`, {
    method: "POST",
    body: { ...input, source_event_id: `user-review:${crypto.randomUUID()}` },
  });
  return res.data;
}
